import {
  Avatar,
  Box,
  Card,
  CardContent,
  Chip,
  Grid,
  IconButton,
  MenuItem,
  Pagination,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTheme } from "@emotion/react";
import { useTranslation } from "react-i18next";
import { tokens } from "../../theme";
import TenantService from "../../services/tenantService";
import {
  DataGrid,
} from "@mui/x-data-grid";
import { useNavigate } from "react-router-dom";
import TenantLayout from "../../layouts/TenantLayout";
import CollapsibleListFilters, {
  ListFiltersExcelButton,
  listFiltersSelectProps,
} from "../../components/CollapsibleListFilters";
import Diversity3OutlinedIcon from "@mui/icons-material/Diversity3Outlined";
import LocalHospitalOutlinedIcon from "@mui/icons-material/LocalHospitalOutlined";
import FolderSharedOutlinedIcon from "@mui/icons-material/FolderSharedOutlined";
import VisibilityIcon from "@mui/icons-material/Visibility";

/** Tenant GET /patients/tenant/all yanıtı Files[] ve fileIds kullanır; file/fileName genelde yoktur. */
function tenantPatientHasLinkedFile(patient) {
  if (Array.isArray(patient?.Files) && patient.Files.length > 0) return true;
  const ids = Array.isArray(patient?.fileIds) ? patient.fileIds.filter(Boolean) : [];
  if (ids.length > 0) return true;
  if (patient?.file && patient.file !== "N/A" && patient.file !== "—") return true;
  if (patient?.fileName) return true;
  return false;
}

/** Bağlı dosya sayısı: önce fileIds (hastanın üye olduğu dosyalar), yoksa Files uzunluğu */
function getTenantPatientLinkedFileCount(patient) {
  const ids = Array.isArray(patient?.fileIds) ? patient.fileIds.filter(Boolean) : [];
  if (ids.length > 0) return ids.length;
  if (Array.isArray(patient?.Files) && patient.Files.length > 0) return patient.Files.length;
  if (patient?.file && patient.file !== "N/A" && patient.file !== "—") return 1;
  if (patient?.fileName) return 1;
  return 0;
}

/**
 * Tenant hasta listesi (view=list) API'si çoğunlukla `Doctor` ilişkisini gönderir; düz `doctorName` gelmeyebilir.
 */
function getTenantPatientDoctorLabel(patient) {
  const raw = patient?.doctorName;
  if (raw != null && String(raw).trim() !== "" && String(raw).trim() !== "N/A") {
    return String(raw).trim();
  }
  const d = patient?.Doctor || patient?.doctor;
  if (!d) return "N/A";
  const combined = `${d.name ?? ""} ${d.surname ?? ""}`.trim();
  return combined || "N/A";
}

export default function TenantPatients() {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { t } = useTranslation(["tenantDashboard", "team"]);
  const tenantService = useMemo(() => new TenantService(), []);
  const navigate = useNavigate();
  const token = localStorage.getItem("tokenTenant");
  const [tablePatients, setTablePatients] = useState([]);
  const [allPatientsStats, setAllPatientsStats] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [sortBy, setSortBy] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("DESC");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [filters, setFilters] = useState({
    patientName: "",
    email: "",
    doctorName: "",
    gender: "",
    dateFrom: "",
    dateTo: "",
  });
  const [appliedFilters, setAppliedFilters] = useState({});
  const [isExporting, setIsExporting] = useState(false);

  const TotalFilesChipCell = ({ count }) => (
    <Chip
      label={count > 0 ? String(count) : t("noFile")}
      sx={{
        minWidth: 40,
        justifyContent: "center",
        px: 1.5,
        py: 0.5,
        borderRadius: 2,
        fontWeight: 700,
        fontVariantNumeric: "tabular-nums",
        background:
          count > 0
            ? `linear-gradient(135deg, ${colors.blueAccent[600]}55, ${colors.blueAccent[500]}aa)`
            : `${colors.grey[700]}66`,
        color: colors.grey[100],
        boxShadow: `0 6px 16px rgba(15, 23, 42, 0.2)`,
      }}
    />
  );

  const DoctorChipCell = ({ value }) => (
    <Chip
      label={value && value !== "N/A" ? value : t('unassigned')}
      sx={{
        px: 1.5,
        py: 0.5,
        borderRadius: 2,
        fontWeight: 600,
        background: value && value !== "N/A"
          ? `linear-gradient(135deg, ${colors.greenAccent[500]}55, ${colors.greenAccent[500]}aa)`
          : `${colors.primary[400]}88`,
        color: colors.grey[100],
        boxShadow: `0 6px 16px rgba(15, 23, 42, 0.2)`,
      }}
    />
  );

  const columns = useMemo(() => [
    {
      field: "firstName",
      headerName: t('columnName'),
      cellClassName: "name-column--cell",
      flex: 0.9,
    },
    {
      field: "lastName",
      headerName: t('columnLastName'),
      cellClassName: "name-column--cell",
      flex: 0.9,
    },
    {
      field: "email",
      headerName: t('columnEmail'),
      flex: 1.1,
    },
    {
      field: "dateOfBirth",
      headerName: t('columnDateOfBirth'),
      flex: 1,
    },
    {
      field: "gender",
      headerName: t('columnGender'),
      flex: 0.7,
    },
    {
      field: "doctorName",
      headerName: t('columnDoctor'),
      flex: 1,
      renderCell: (params) => <DoctorChipCell value={params.value} />,
    },
    {
      field: "totalFiles",
      headerName: t("columnTotalFiles"),
      flex: 0.65,
      minWidth: 110,
      type: "number",
      align: "center",
      headerAlign: "center",
      renderCell: (params) => (
        <TotalFilesChipCell count={Number(params.value) || 0} />
      ),
    },
    {
      field: "actions",
      headerName: t("team:actions"),
      width: 88,
      minWidth: 88,
      sortable: false,
      filterable: false,
      disableExport: true,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => (
        <Tooltip title={t("team:viewProfile")}>
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/tenant/patients/${params.id}`);
            }}
            sx={{
              color: colors.blueAccent[400],
              "&:hover": {
                backgroundColor: `${colors.blueAccent[700]}33`,
              },
            }}
            aria-label={t("team:viewProfile")}
          >
            <VisibilityIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    },
  ], [t, colors, navigate]);

  const handleRowClick = (params) => {
    console.log("Seçilen Satır Params:", params);
    console.log("Seçilen Hasta ID:", params.id);
    navigate(`/tenant/patients/${params.id}`);
  };

  const buildCleanFilters = useCallback((source) => {
    const next = {};
    Object.entries(source).forEach(([key, value]) => {
      if (value !== undefined && value !== null && String(value).trim() !== "") {
        next[key] = String(value).trim();
      }
    });
    return next;
  }, []);

  const fetchPatientsTable = useCallback(async () => {
    try {
      setLoading(true);
      const response = await tenantService.getTenantPatientsList(token, {
        withMeta: true,
        params: appliedFilters,
        headers: {
          "x-bone-pageindex": pageIndex,
          "x-bone-limit": pageSize,
          "x-bone-sortby": sortBy,
          "x-bone-sortorder": sortOrder,
        },
      });
      setTablePatients(Array.isArray(response?.data) ? response.data : []);
      setTotalCount(Number(response?.headers?.["x-bone-totalcount"]) || 0);
      setTotalPages(Math.max(Number(response?.headers?.["x-bone-totalpage"]) || 1, 1));
    } catch (error) {
      setTablePatients([]);
      setTotalCount(0);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  }, [tenantService, token, appliedFilters, pageIndex, pageSize, sortBy, sortOrder]);

  useEffect(() => {
    fetchPatientsTable();
  }, [fetchPatientsTable]);

  useEffect(() => {
    const fetchPatientStats = async () => {
      try {
        const response = await tenantService.getAllPatients(token);
        const formattedPatients = (response?.patients || []).map((patient) => ({
          ...patient,
          doctorName: patient.Doctor
            ? `${patient.Doctor.name} ${patient.Doctor.surname}`
            : "N/A",
        }));
        setAllPatientsStats(formattedPatients);
      } catch (error) {
        setAllPatientsStats([]);
      }
    };
    fetchPatientStats();
  }, [tenantService, token]);

  const processedRows = useMemo(() => {
    return tablePatients.map((patient) => ({
      id: patient.id,
      firstName: patient.firstName || patient.name || "—",
      lastName: patient.lastName || patient.surname || "—",
      email: patient.email || "—",
      totalFiles: getTenantPatientLinkedFileCount(patient),
      dateOfBirth: patient.dateOfBirth || patient.birthDate || "—",
      gender: patient.gender || "—",
      doctorName: getTenantPatientDoctorLabel(patient),
    }));
  }, [tablePatients]);

  const summaryCards = useMemo(() => {
    const total = allPatientsStats.length || totalCount;
    const assignedDoctor = allPatientsStats.filter(
      (patient) => patient.doctorName && patient.doctorName !== "N/A"
    ).length;
    const withFile = allPatientsStats.filter(tenantPatientHasLinkedFile).length;

    return [
      {
        label: t('totalPatients'),
        value: total,
        sublabel: t('totalPatientsSublabel'),
        icon: <Diversity3OutlinedIcon sx={{ fontSize: 28 }} />,
        gradient: `linear-gradient(135deg, ${colors.blueAccent[600]} 0%, ${colors.greenAccent[500]} 100%)`,
      },
      {
        label: t('assignedDoctors'),
        value: assignedDoctor,
        sublabel: t('assignedDoctorsSublabel'),
        icon: <LocalHospitalOutlinedIcon sx={{ fontSize: 28 }} />,
        gradient: `linear-gradient(135deg, ${colors.greenAccent[500]} 0%, ${colors.blueAccent[600]} 100%)`,
      },
      {
        label: t('patientsWithFile'),
        value: withFile,
        sublabel: t('patientsWithFileSublabel'),
        icon: <FolderSharedOutlinedIcon sx={{ fontSize: 28 }} />,
        gradient: `linear-gradient(135deg, ${colors.blueAccent[700]} 0%, ${colors.primary[600]} 100%)`,
      },
    ];
  }, [allPatientsStats, totalCount, colors, t]);

  const activeFilterCount = useMemo(
    () => Object.keys(appliedFilters).length,
    [appliedFilters]
  );

  const handleApplyFilters = () => {
    setPageIndex(0);
    setAppliedFilters(buildCleanFilters(filters));
  };

  const handleClearFilters = () => {
    setFilters({
      patientName: "",
      email: "",
      doctorName: "",
      gender: "",
      dateFrom: "",
      dateTo: "",
    });
    setPageIndex(0);
    setAppliedFilters({});
  };

  const handleExportExcel = async () => {
    try {
      setIsExporting(true);
      const response = await tenantService.exportTenantPatientsExcel(token, appliedFilters);
      const blob = new Blob(
        [response.data],
        { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }
      );
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      const disposition = response.headers?.["content-disposition"] || "";
      const fileNameMatch = disposition.match(/filename="(.+)"/);
      link.href = url;
      link.download = fileNameMatch?.[1] || "tenant-patients-export.xlsx";
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.log("Excel export failed.");
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <TenantLayout
      contentSx={{
        display: "flex",
        flexDirection: "column",
        gap: 4,
        backgroundColor: "#f5f7fb",
      }}
    >
      <Grid
        container
        spacing={3}
        sx={{ width: "100%", margin: "0 auto", maxWidth: "1200px" }}
      >
        {summaryCards.map((card) => (
          <Grid item xs={12} sm={6} md={4} key={card.label}>
            <Card
              sx={{
                height: "100%",
                borderRadius: 4,
                background: card.gradient,
                color: colors.grey[100],
                boxShadow: `0 20px 40px rgba(15, 23, 42, 0.25)`,
              }}
            >
              <CardContent sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Avatar
                    sx={{
                      width: 48,
                      height: 48,
                      backgroundColor: "rgba(255,255,255,0.25)",
                      color: "white",
                      backdropFilter: "blur(4px)",
                    }}
                  >
                    {card.icon}
                  </Avatar>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      {card.label}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.85 }}>
                      {card.sublabel}
                    </Typography>
                  </Box>
                </Stack>
                <Typography variant="h3" sx={{ fontWeight: 800 }}>
                  {card.value}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Box
        sx={{
          width: "100%",
          maxWidth: "1200px",
          margin: "0 auto",
          backgroundColor: colors.primary[500],
          borderRadius: 4,
          boxShadow: `0 24px 48px rgba(15, 23, 42, 0.35)`,
          border: `1px solid ${colors.primary[300]}55`,
          p: { xs: 2, md: 3 },
        }}
      >
        <Typography
          variant="h5"
          sx={{
            mb: 2,
            fontWeight: 700,
            color: colors.grey[100],
            letterSpacing: 0.4,
          }}
        >
          {t('patientOverview')}
        </Typography>

        <CollapsibleListFilters
          colors={colors}
          open={filtersOpen}
          onToggle={() => setFiltersOpen((prev) => !prev)}
          activeFilterCount={activeFilterCount}
          onApply={handleApplyFilters}
          onClear={handleClearFilters}
          exportAction={
            <ListFiltersExcelButton
              colors={colors}
              onClick={handleExportExcel}
              disabled={isExporting}
              loading={isExporting}
            />
          }
        >
          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(12, 1fr)", gap: 2 }}>
            <Box sx={{ gridColumn: { xs: "span 12", md: "span 3" } }}>
              <TextField
                fullWidth
                size="small"
                label="Patient Name"
                value={filters.patientName}
                onChange={(e) => setFilters((prev) => ({ ...prev, patientName: e.target.value }))}
              />
            </Box>
            <Box sx={{ gridColumn: { xs: "span 12", md: "span 3" } }}>
              <TextField
                fullWidth
                size="small"
                label="Email"
                value={filters.email}
                onChange={(e) => setFilters((prev) => ({ ...prev, email: e.target.value }))}
              />
            </Box>
            <Box sx={{ gridColumn: { xs: "span 12", md: "span 3" } }}>
              <TextField
                fullWidth
                size="small"
                label="Doctor Name"
                value={filters.doctorName}
                onChange={(e) => setFilters((prev) => ({ ...prev, doctorName: e.target.value }))}
              />
            </Box>
            <Box sx={{ gridColumn: { xs: "span 12", md: "span 3" } }}>
              <TextField
                select
                fullWidth
                size="small"
                label="Gender"
                value={filters.gender}
                onChange={(e) => setFilters((prev) => ({ ...prev, gender: e.target.value }))}
                SelectProps={listFiltersSelectProps(colors)}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="male">Male</MenuItem>
                <MenuItem value="female">Female</MenuItem>
                <MenuItem value="other">Other</MenuItem>
              </TextField>
            </Box>
            <Box sx={{ gridColumn: { xs: "span 12", md: "span 3" } }}>
              <TextField
                fullWidth
                size="small"
                label="Date From"
                type="date"
                value={filters.dateFrom}
                onChange={(e) => setFilters((prev) => ({ ...prev, dateFrom: e.target.value }))}
                InputLabelProps={{ shrink: true }}
              />
            </Box>
            <Box sx={{ gridColumn: { xs: "span 12", md: "span 3" } }}>
              <TextField
                fullWidth
                size="small"
                label="Date To"
                type="date"
                value={filters.dateTo}
                onChange={(e) => setFilters((prev) => ({ ...prev, dateTo: e.target.value }))}
                InputLabelProps={{ shrink: true }}
              />
            </Box>
          </Box>
        </CollapsibleListFilters>

        <DataGrid
          rows={processedRows}
          columns={columns}
          disableRowSelectionOnClick
          autoHeight
          loading={loading}
          pagination
          paginationMode="server"
          rowCount={totalCount}
          paginationModel={{ page: pageIndex, pageSize }}
          onPaginationModelChange={(model) => {
            if (model.page !== pageIndex) setPageIndex(model.page);
            if (model.pageSize !== pageSize) {
              setPageIndex(0);
              setPageSize(model.pageSize);
            }
          }}
          pageSizeOptions={[10, 25, 50]}
          sx={{
            border: "none",
            color: colors.grey[100],
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: `${colors.blueAccent[700]}80`,
              borderBottom: "none",
              fontWeight: 700,
              letterSpacing: 0.4,
            },
            '& .MuiDataGrid-virtualScroller': {
              backgroundColor: colors.primary[500],
            },
            '& .MuiDataGrid-row:hover': {
              backgroundColor: `${colors.blueAccent[700]}33`,
            },
            '& .MuiDataGrid-cell': {
              borderBottom: `1px solid ${colors.primary[400]}55`,
            },
            '& .MuiDataGrid-footerContainer': {
              backgroundColor: `${colors.blueAccent[700]}40`,
              borderTop: `1px solid ${colors.primary[400]}55`,
            },
            '& .MuiTablePagination-root': {
              color: colors.grey[200],
            },
            '& .MuiTablePagination-selectIcon': {
              color: colors.grey[200],
            },
          }}
          onRowClick={handleRowClick}
        />

        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={1.5}
          alignItems={{ xs: "stretch", md: "center" }}
          justifyContent="space-between"
          sx={{ mt: 2 }}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <Chip label={`Total: ${totalCount}`} size="small" />
            <Chip label={`Page: ${Math.min(pageIndex + 1, totalPages)} / ${totalPages}`} size="small" />
          </Stack>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
            <TextField
              select
              size="small"
              label="Sort By"
              value={sortBy}
              onChange={(e) => {
                setPageIndex(0);
                setSortBy(e.target.value);
              }}
              sx={{ minWidth: 150 }}
            >
              <MenuItem value="createdAt">Created</MenuItem>
              <MenuItem value="firstName">First Name</MenuItem>
              <MenuItem value="lastName">Last Name</MenuItem>
              <MenuItem value="email">Email</MenuItem>
            </TextField>
            <TextField
              select
              size="small"
              label="Direction"
              value={sortOrder}
              onChange={(e) => {
                setPageIndex(0);
                setSortOrder(e.target.value);
              }}
              sx={{ minWidth: 130 }}
            >
              <MenuItem value="DESC">DESC</MenuItem>
              <MenuItem value="ASC">ASC</MenuItem>
            </TextField>
          </Stack>
          <Pagination
            color="primary"
            shape="rounded"
            page={Math.min(pageIndex + 1, totalPages)}
            count={totalPages}
            onChange={(_, value) => setPageIndex(value - 1)}
          />
        </Stack>
      </Box>
    </TenantLayout>
  );
}
